type Type = 'R' | 'W';

type DiscountPolicyType = {
  discount_rate: number;
  discount_type: Type;
  max_discount_price: number;
};

type ReservePolicyType = {
  reserve_rate: number;
  reserve_type: Type;
};

type PolicyType = DiscountPolicyType & ReservePolicyType;

type CalculateDiscountType = (
  price: number,
  num: number,
  policy: DiscountPolicyType,
) => number;

type CalculateReserveType = (
  price: number,
  num: number,
  policy: ReservePolicyType,
) => number;

type CalculateDiscountReserveType = (
  price: number,
  num: number,
  policy: PolicyType,
) => {
  discount: number;
  reserve: number;
};

type CalculateDiscountRate = (
  originPrice: number,
  discountedPrice: number,
) => number;

/**
 * 할인 비율 계산
 * --
 * @param originPrice 할인 전 원금
 * @param discountedPrice 할인 후 금액
 */
const calculateDiscountRate: CalculateDiscountRate = (
  originPrice,
  discountedPrice,
) => {
  if (!originPrice || originPrice <= 0) {
    return 0;
  }

  const rate = (discountedPrice / originPrice) * 100;
  const discountRate = Math.round(100 - rate);

  return discountRate;
};

/**
 * 할인금 계산
 * --
 * @param price 상품 개별 단가
 * @param num 상품 구매 수량
 * @param reservePolicy 할인 정책
 * @returns 상품 전체 할인금
 */
const calculateDiscount: CalculateDiscountType = (
  price,
  num,
  discountPolicy,
): number => {
  let discount = 0;
  const { discount_rate, discount_type, max_discount_price } = discountPolicy;

  if (discount_type === 'W') {
    discount = discount_rate;
  } else if (discount_type === 'R') {
    discount = (price * discount_rate) / 100;
  } else {
    discount = 0;
  }

  // 할인금 예외처리
  discount = Math.max(0, discount);

  // 할인금 10원단위 절삭
  discount = Math.floor(discount / 10) * 10;

  // 정액 할인은 1회 적용, 정률 할인은 전체 수량 금액 기준으로 적용
  const totalDiscount = discount_type === 'R' ? discount * num : discount;

  // 최대할인금 예외처리
  return Math.min(totalDiscount, max_discount_price ?? totalDiscount);
};

/**
 * 적립금 계산
 * --
 * @param price 상품 개별 단가
 * @param num 상품 구매 수량
 * @param reservePolicy 적립금 정책
 * @returns 상품 전체 적립금
 */
const calculateReserve: CalculateReserveType = (price, num, reservePolicy) => {
  let reserve = 0;

  const { reserve_rate, reserve_type } = reservePolicy;

  if (reserve_type === 'W') {
    reserve = reserve_rate * num;
  } else if (reserve_type === 'R') {
    const total = price * num;

    reserve = (total * reserve_rate) / 100;
  } else {
    reserve = 0;
  }

  reserve = Math.floor(reserve);

  return Math.max(0, reserve);
};

/**
 * 상품 할인금/적립금 계산
 * --
 * @param price 상품 개별 단가
 * @param num 상품 구매 수량
 * @param policy 정책
 * @returns 전체 할인금/적립금
 */
const calculateDiscountReserve: CalculateDiscountReserveType = (
  price,
  num,
  policy,
) => {
  let discount = 0;
  let reserve = 0;

  const {
    discount_rate,
    discount_type,
    reserve_rate,
    reserve_type,
    max_discount_price,
  } = policy;

  // 전체 할인금액 계산
  discount = calculateDiscount(price, num, {
    discount_rate,
    discount_type,
    max_discount_price,
  });

  // 할인 적용 후 개별 단가
  const perDiscountedPrice = (price * num - discount) / num;

  // 할인이 적용된 가격에서 전체 적립금 게산
  reserve = calculateReserve(perDiscountedPrice, num, {
    reserve_rate,
    reserve_type,
  });

  return {
    discount,
    reserve,
  };
};

const Calculate = {
  calculateDiscount,
  calculateReserve,
  calculateDiscountReserve,
  calculateDiscountRate,
};

export default Calculate;
